import React, { useState, useEffect } from 'react';
import VideoPlayer from './VideoPlayer';
import QuestionOptions from './QuestionOptions';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';
import { encodeJWT } from '../utils/jwtHelper';

const STEPS_DATA = [
  {
    id: 1,
    videoUrl: 'https://cdn.lovescape.com/cdn/front/videos/marketing/1talk.mp4',
    heading: 'Tell me… what age range feels just right to you?',
    options: [
      { label: '18–25', value: '18' },
      { label: '26–32', value: '32' },
      { label: '33–40', value: '33' },
      { label: '40+', value: '40' }
    ],
    autoAdvance: false,
    dataKey: 'age'
  },
  {
    id: 2,
    videoUrl: 'https://cdn.lovescape.com/cdn/front/videos/marketing/2talk.mp4',
    heading: 'Are you more into the active, always-on-the-go type… or someone who loves cozy nights in?',
    options: [
      { label: 'Definitely the active one', value: 'active' },
      { label: 'Cozy nights, movies, and cuddles sound perfect.', value: 'cozy' },
      { label: 'A mix of both — gym together, then Netflix later.', value: 'balanced' },
      { label: "Lifestyle doesn't matter if we click.", value: 'doesnt-matter' }
    ],
    autoAdvance: false,
    dataKey: 'lifestylePreference'
  },
  {
    id: 3,
    videoUrl: 'https://cdn.lovescape.com/cdn/front/videos/marketing/3talk.mp4',
    heading: "How important is a woman's sense of humor to you?",
    options: [
      { label: 'Very important', value: 'very-important' },
      { label: 'A little humor is nice', value: 'moderate' },
      { label: 'Give me sarcasm and quick wit any day.', value: 'sarcastic' },
      { label: 'If she laughs at my jokes, we are good!', value: 'mutual-laughs' }
    ],
    autoAdvance: false,
    dataKey: 'humorPreference'
  },
  {
    id: 4,
    videoUrl: 'https://cdn.lovescape.com/cdn/front/videos/marketing/4talk.mp4',
    heading: "Be honest… do you notice a woman's hair first?",
    options: [
      { label: 'Long', value: 'long' },
      { label: 'Short', value: 'short' },
      { label: 'Curly', value: 'curly' },
      { label: "Doesn't matter", value: 'smile' }
    ],
    autoAdvance: false,
    dataKey: 'hairColor'
  },
  {
    id: 5,
    videoUrl: 'https://cdn.lovescape.com/cdn/front/videos/marketing/5talk.mp4',
    heading: "If you could describe your ideal woman's personality in one word…",
    options: [
      { label: 'Kind ❤️', value: 'kind' },
      { label: 'Confident 💪', value: 'confident' },
      { label: 'Playful 😉', value: 'playful' },
      { label: 'Loyal 💞', value: 'loyal' },
      { label: 'Mysterious 🔥', value: 'mysterious' }
    ],
    autoAdvance: false,
    dataKey: 'personality',
    finalStep: true
  }
];

const VideoQuestionnaire = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [userData, setUserData] = useState({
    age: '',
    extraFields: {
      lifestylePreference: '',
      humorPreference: '',
      hairColor: '',
      personality: ''
    }
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSoundPopup, setShowSoundPopup] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true);

  const currentStepData = STEPS_DATA[currentStep];

  const handleSoundChoice = (enableSound) => {
    setVideoMuted(!enableSound);
    setShowSoundPopup(false);
    
    // Track sound choice with GA4
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        button_name: enableSound ? 'sound_yes' : 'sound_no'
      });
    }
  };

  useEffect(() => {
    setShowOptions(false);
    setSelectedOption(null);
    const timer = setTimeout(() => setShowOptions(true), 800);
    return () => clearTimeout(timer);
  }, [currentStep]);

  // Track page view for each step change
  useEffect(() => {
    if (window.gtag && typeof currentStep === 'number') {
      window.gtag('event', 'page_view', {
        page_title: `Onboarding Step ${currentStep + 1}`,
        page_location: window.location.href,
        page_path: `/step-${currentStep + 1}`
      });
    }
  }, [currentStep]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option.value);

    // Update userData
    const newUserData = { ...userData };
    if (currentStepData.dataKey === 'age') {
      newUserData.age = option.value;
    } else {
      newUserData.extraFields[currentStepData.dataKey] = option.value;
    }
    setUserData(newUserData);

    // Auto-advance if configured
    if (currentStepData.autoAdvance) {
      setTimeout(() => {
        if (currentStep < STEPS_DATA.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      }, 500);
    }
  };

  const handleNext = () => {
    if (!selectedOption) return;
    
    // Track Next button click with GA4
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        button_name: 'next',
        step: currentStep + 1
      });
    }
    
    if (currentStepData.finalStep) {
      handleCreateAvatar();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleCreateAvatar = () => {
    setIsProcessing(true);
    
    // Track Create Avatar button click with GA4
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        button_name: 'create_avatar'
      });
    }
    
    // Prepare final JSON
    const finalJson = {
      imageStyle: 'realistic',
      gender: 'female',
      safetyLevel: 'nude',
      age: parseInt(userData.age),
      extraFields: {
        hairColor: userData.extraFields.hairColor,
        personality: userData.extraFields.personality,
        lifestylePreference: userData.extraFields.lifestylePreference,
        humorPreference: userData.extraFields.humorPreference
      }
    };

    // Show processing screen
    setCurrentStep('processing');

    // Redirect after 3 seconds
    setTimeout(() => {
      const secret = '2u0O4I&>m|n|5P"Nwvi0SlimSteam#';
      
      // Using custom JWT helper
      try {
        const token = encodeJWT(finalJson, secret);
        const redirectUrl = `https://lovescape.com/character-pro/avatar/${token}?campaignId=talkingheads&sourceId=facebook&userId=847e9325a3b39b98e2a56ad49c76af2b3e064c81f7bae1a6f8173a74c292fffc`;
        
        console.log('Redirecting to:', redirectUrl);
        console.log('Final JSON:', finalJson);
        console.log('Generated Token:', token);
        
        // Automatic redirect after 3 seconds
        window.location.href = redirectUrl;
      } catch (error) {
        console.error('JWT encoding error:', error);
      }
    }, 3000);
  };

  if (currentStep === 'processing') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-6 animate-fade-in">
          <div className="relative w-[700px] max-w-[90vw] aspect-video mx-auto rounded-lg overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/20 via-black to-[#FFD700]/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-16 h-16 text-[#FFD700] animate-spin" />
            </div>
          </div>
          <h2 className="text-4xl text-white font-reinder">Summarizing Your Data...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      {/* Sound Popup */}
      {showSoundPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#FFD700]/50 backdrop-blur-sm rounded-lg p-8 max-w-md mx-4 text-center">
            <h3 className="text-2xl font-reinder text-black mb-6">
              Shall we turn on the beautiful voices for you?
            </h3>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => handleSoundChoice(true)}
                className="bg-black hover:bg-black/80 text-[#FFD700] font-semibold px-8 py-3 text-lg rounded-lg"
              >
                Yes
              </Button>
              <Button
                onClick={() => handleSoundChoice(false)}
                className="bg-black hover:bg-black/80 text-[#FFD700] font-semibold px-8 py-3 text-lg rounded-lg"
              >
                No
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-[750px] mx-auto space-y-8">
        {/* Video Player with Gold Frame */}
        <VideoPlayer videoUrl={currentStepData.videoUrl} initialMuted={videoMuted} />

        {/* Question and Options */}
        <div className={`space-y-6 transition-opacity duration-700 ${showOptions ? 'opacity-100' : 'opacity-0'}`}>
          <h2 className="text-2xl md:text-3xl text-white text-center font-reinder leading-relaxed px-4">
            {currentStepData.heading}
          </h2>

          <QuestionOptions
            options={currentStepData.options}
            selectedOption={selectedOption}
            onSelect={handleOptionSelect}
          />
        </div>

        {/* Next/Create Avatar Button - Outside opacity wrapper so it stays visible */}
        {!currentStepData.autoAdvance && selectedOption && showOptions && (
          <div className="flex justify-center animate-fade-in">
            <Button
              onClick={handleNext}
              className="bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105"
            >
              {currentStepData.finalStep ? 'Create Avatar' : 'Next'}
            </Button>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="flex justify-center gap-2 pt-4">
          {STEPS_DATA.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep
                  ? 'w-8 bg-[#FFD700]'
                  : index < currentStep
                  ? 'w-2 bg-[#FFD700]/60'
                  : 'w-2 bg-gray-700'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default VideoQuestionnaire;